import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import { NoteType } from 'models/projectNote'
import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'

export interface TaskAnchorContext {
    isArticle: boolean
    noteType: NoteType.ProductType | NoteType.Family
    isProductTypeProperty: true | undefined
    contentItemId: string
    projectElementId: string | undefined
    projectProductFamilyId: string | undefined
    projectElementDescription: string
}

interface UseTasksPopoverOptions {
    selectedMainMenuNode: Partial<ContentItemViewNodesType> | null | undefined
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    selectedProjectElementNode: Partial<ProjectElementNode>
}

interface UseTasksPopoverResult {
    tasksAnchorEl: Element | null
    isTasksSubmenuOpen: boolean
    handleTasksMenuOpen: (e: React.MouseEvent<Element>) => void
    handleTasksMenuClose: () => void
    openTasksWindow: (params?: Record<string, string>) => void
    taskAnchorContext: TaskAnchorContext | null
}

export const useTasksPopover = ({
    selectedMainMenuNode,
    contentItemViewNodes,
    selectedProjectElementNode,
}: UseTasksPopoverOptions): UseTasksPopoverResult => {
    const { projectId } = useParams<{ projectId?: string }>()

    const openTasksWindow = React.useCallback(
        (params?: Record<string, string>) => {
            if (!projectId) return
            const query =
                params && Object.keys(params).length > 0
                    ? '?' + new URLSearchParams(params).toString()
                    : ''
            window.open(
                `/projects/${projectId}/projecttasks${query}`,
                `ProjectTasks${projectId}`,
                `directories=no,titlebar=no,location=no,toolbar=no,menubar=no,resizable=yes,height=${window.screen.height},width=${window.screen.width}`
            )
        },
        [projectId]
    )

    const [tasksAnchorEl, setTasksAnchorEl] = React.useState<Element | null>(
        null
    )
    const isTasksSubmenuOpen = Boolean(tasksAnchorEl)

    const handleTasksMenuOpen = React.useCallback(
        (e: React.MouseEvent<Element>) => setTasksAnchorEl(e.currentTarget),
        []
    )
    const handleTasksMenuClose = React.useCallback(
        () => setTasksAnchorEl(null),
        []
    )

    // Derive task anchor context from the hovered element — mirrors classic HoveredItemActions
    const taskAnchorContext = useMemo((): TaskAnchorContext | null => {
        if (!selectedMainMenuNode) return null
        const nodeId =
            selectedMainMenuNode.dtvContentItemId || selectedMainMenuNode.id
        const currentNode = contentItemViewNodes.find((n) => n.id === nodeId)
        if (!currentNode) return null
        const isArticle = currentNode.textType === 'Article'
        const elementId = selectedProjectElementNode?.elementId ?? undefined

        // noteType is driven by the SELECTED PROJECT ELEMENT TYPE — not article vs paragraph.
        // Classic: PRODUCTTYPE element → NoteType.ProductType, else → NoteType.Family.
        const elementType = selectedProjectElementNode?.elementType ?? ''
        const isProductTypeElement = elementType === 'PRODUCTTYPE'

        // treePath format: "A1010|Division Name||030130|SECTION TITLE" → last segment → "030130 - SECTION TITLE"
        const treePath = selectedProjectElementNode?.treePath ?? ''
        const treeSegments = treePath.split('||').filter(Boolean)
        const lastSegment = treeSegments[treeSegments.length - 1] ?? ''
        const sectionLabel = lastSegment.split('|').filter(Boolean).join(' - ')

        const projectElementDescription = isProductTypeElement
            ? sectionLabel
                ? `Product Type - ${sectionLabel}`
                : ''
            : sectionLabel
            ? `Family ${sectionLabel}`
            : ''

        return {
            isArticle,
            noteType: isProductTypeElement
                ? NoteType.ProductType
                : NoteType.Family,
            // Classic only sends isProductTypeProperty for PRODUCTTYPE elements
            isProductTypeProperty: isProductTypeElement ? true : undefined,
            contentItemId: nodeId as string,
            projectElementId: elementId as string | undefined,
            projectProductFamilyId: currentNode.productFamilyId ?? undefined,
            projectElementDescription,
        }
    }, [selectedMainMenuNode, contentItemViewNodes, selectedProjectElementNode])

    return {
        tasksAnchorEl,
        isTasksSubmenuOpen,
        handleTasksMenuOpen,
        handleTasksMenuClose,
        openTasksWindow,
        taskAnchorContext,
    }
}
